import React, { useState, useContext, useEffect } from 'react';
import { ReactDrawer } from '@parts';
import { useMount } from 'ahooks';
import { motion } from 'framer-motion/dist/framer-motion';
import classnames from 'classnames';
import { slugify } from '@utils';
import ComponentProvider, { ComponentContext } from './ComponentProvider';
import usePortal from 'react-cool-portal';

const MenuItem = ({ parent, link, level, path, highlight }) => {
  const { currentNavState } = useContext(ComponentContext);
  const [currentNav, setCurrentNav] = currentNavState

  const optionClasses = classnames('Nav__item', `level-${level}`, {
    'is-active': link.active,
    'has-submenu': link.links,
    'u-hidden': link.object_handle == 'flag-how-to-videos' && level == 3
  });

  const handleClick = (e) => {
    if (link.links) {
      e.preventDefault()

      setCurrentNav((prev) => {
        const i = [...prev]
        i.splice(0, 0, path);
        return i
      })
    }
  };

  return (
    <>
      <div className={optionClasses}>
        <motion.a initial={false} onClick={handleClick} href={link.url} className={`Nav__link level-${level}`}>
          <span>{link.title}</span>
          {link.links &&
            <svg viewBox="0 0 20 20" className="icon" width="1em" height="1em">
              <use href="#chevron-right"></use>
            </svg>
          }
        </motion.a>
      </div>
      {link.links && (
        <SubMenu items={link.links} parent={link} level={level} path={path} highlight={highlight}></SubMenu>
      )}
    </>
  );
};

const SubMenu = ({ items, level, parent, path, highlight }) => {
  const { currentNavState } = useContext(ComponentContext);
  const [currentNav, setCurrentNav] = currentNavState

  const { Portal, isShow, show, hide, toggle } = usePortal({
    defaultShow: true,
    clickOutsideToHide: false,
    escToHide: false,
    containerId: 'subNav__container',
  });

  const handlePrev = (e) => {
    setCurrentNav((prev) => {
      const i = [...prev]
      i.shift();
      return i
    })
  };

  return (
    <Portal>
      <motion.div
        className={`Nav__submenu level-${level}`} data-identifier={path}
        key={`submenu_${path}`}
        initial="right"
        animate={currentNav[0] == path ? 'open' : currentNav.includes(path) ? 'left' : 'right'}
        exit="right"
        variants={{
          open: { display: "block", opacity: 1, x: 0 },
          right: { opacity: 0, x: '100%' },
          left: { opacity: 0, x: '-100%' },
        }}
        transition={{ duration: 0.6, ease: [0.04, 0.62, 0.23, 0.98] }}
      >
        <div className="Nav__item Nav__item_prev" onClick={handlePrev}>
          <svg viewBox="0 0 20 20" className="icon" width="1em" height="1em">
            <use href="#chevron-left"></use>
          </svg>
          <span>{parent.title}</span></div>
        {
          items.map((link, index) => {
            return (
              <MenuItem parent={parent} link={link} level={level + 1} key={link.title} path={`${path}_${slugify(link.title)}`}></MenuItem>
            );
          })
        }
        {
          level == 1 && highlight &&
          <div className="Card Card--navHighlight has-link">

            <figure className="Card__media" data-layout="image-cover">
              <img src={highlight.image} loading="lazy" alt={highlight.title} />
            </figure>
            <div className="Card__content">
              <a href={highlight.cta_link} className="Button" data-style="text blue">{highlight.cta_label}</a>
              <h6 className="Card__title">{highlight.title}</h6>
            </div>
          </div>
        }
      </motion.div >
    </Portal>
  );
};

const MobileNav = ({ parentNode }) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [mainMenu, setMainMenu] = useState({ title: '', links: [] });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [highlights, setHighlights] = useState(null);

  const { currentNavState } = useContext(ComponentContext);
  const [currentNav] = currentNavState

  useMount(() => {
    let contentTag = document.querySelector('[data-menu-content]');
    try {
      let menuData = JSON.parse(contentTag.innerText);
      setHighlights(menuData.highlights)
      setIsLoggedIn(menuData.loggedIn)

      let menuKey = Object.keys(menuData['menu'])[0];
      setMainMenu(menuData['menu'][menuKey]);

    } catch (error) {
      console.log(error);
    }
  });

  const { links } = mainMenu;

  return (
    <>
      <button className="Button mobile-toggler u-show--untilDesktop" data-style="noLayout" onClick={() => setShowDrawer(!showDrawer)}>
        <svg className="icon icon--primary" height="1em" viewBox="0 0 20 20" width="1em">
          <use xlinkHref="#menu"></use>
        </svg>
        <span className="screen-reader-text">Menu</span>
      </button>
      <ReactDrawer className="Drawer--mobileNav" showDrawer={showDrawer} setShowDrawer={setShowDrawer} position={'left'}>
        <header>
          <button type="button" className="Button Button--drawerClose" data-style="noLayout" onClick={() => setShowDrawer(!showDrawer)}>
            <svg className="icon " height="1em" viewBox="0 0 20 20" width="1em">
              <use xlinkHref="#close"></use>
            </svg>
          </button>
          <h4>Shop Colonial Flag</h4>
        </header>
        <div id="subNav__container">
          <motion.div
            className="Nav Nav--mainMobile Nav--vertical"
            key="content"
            initial="open"
            animate={currentNav[0] == 'root' ? 'open' : 'collapsed'}
            exit="collapsed"
            variants={{
              open: { display: 'block', opacity: 1, x: 0 },
              collapsed: { opacity: 0, x: '-100%', transitionEnd: { display: 'none' } },
            }}
            transition={{ duration: 0.6, ease: [0.04, 0.62, 0.23, 0.98] }}
          >


            <div className="Nav Nav--mainMobile Nav--vertical">
              {links.map((link, index) => {
                return <MenuItem link={link} level={1} key={link.title} path={link.object_handle} highlight={highlights?.find(h => h.nav_item == link.title)}></MenuItem>;
              })}
              <div className="Nav__item level-1">
                <a href="/account" className="Nav__link level-1">{isLoggedIn ? 'My Account' : 'Sign In'}</a>
              </div>
            </div>

          </motion.div>
        </div>
      </ReactDrawer>
    </>
  );
};

const MobileNavMain = (props) => {
  return (
    <ComponentProvider {...props}>
      <MobileNav {...props} />
    </ComponentProvider>
  )
}

export default MobileNavMain;
